/**
 * LDB Duel — Custom Card Sleeve Designer
 * Procedural 3D sleeve preview + artwork placement tool.
 *
 * Architecture notes for future maintainers:
 *  - three.js is loaded as an ES module via dynamic import(). The URL is passed
 *    in on a data attribute because Liquid cannot be used inside .js assets.
 *  - The sleeve is generated in code, not loaded from a .glb. Finish styles are
 *    material parameters, so variant selection changes the render live.
 *  - The printed face is a separate flat mesh sitting just outside the opaque
 *    base slab. This matters: the base slab's inner surface must stay plain
 *    black/white, because you see it through the transparent front.
 *  - The product form is re-rendered by product-info.js on every variant change
 *    (it replaces the innerHTML of #ProductBuyButtons). Any input we inject is
 *    therefore destroyed on each variant switch and must be re-injected. See
 *    ensureFormInputs().
 */
(function () {
  'use strict';

  var MM_PER_INCH = 25.4;
  var PX_PER_MM = 20;           // texture resolution: 66mm -> 1320px
  var LOW_DPI_THRESHOLD = 150;
  var STACK_BREAKPOINT = 1199;  // must match the media query in the CSS


  var HOLO_VERT = [
    'varying vec3 vNormalW;',
    'varying vec3 vViewW;',
    'varying vec2 vUvH;',
    'void main() {',
    '  vUvH = uv;',
    '  vec4 wp = modelMatrix * vec4(position, 1.0);',
    '  vNormalW = normalize(mat3(modelMatrix) * normal);',
    '  vViewW = normalize(cameraPosition - wp.xyz);',
    '  gl_Position = projectionMatrix * viewMatrix * wp;',
    '}'
  ].join('\n');

  var HOLO_FRAG = [
    'uniform float uIntensity;',
    'varying vec3 vNormalW;',
    'varying vec3 vViewW;',
    'varying vec2 vUvH;',
    'vec3 hue2rgb(float h) {',
    '  vec3 k = mod(vec3(5.0, 3.0, 1.0) + h * 6.0, 6.0);',
    '  return clamp(min(k, 4.0 - k), 0.0, 1.0);',
    '}',
    'void main() {',
    // 0 when looking straight on, 1 at a grazing angle
    '  float f = 1.0 - abs(dot(normalize(vNormalW), normalize(vViewW)));',
    '  float h = fract(f * 2.3 + vUvH.y * 1.35 + vUvH.x * 0.65);',
    // Darker sheen (2026-09-17): the additive rainbow used to sit on top of the
    // art even face-on (0.30 floor), washing the holo preview out. The floor is
    // now near zero and the ramp gentler, so the effect reads as a dark
    // iridescent film that lights up on tilt. The sin() diffraction-line
    // pattern is gone — on the small preview it aliased into stripes.
    '  float band = 0.06 + 0.44 * pow(f, 0.9);',
    '  vec3 c = hue2rgb(h) * band * uIntensity;',
    '  gl_FragColor = vec4(c, 1.0);',
    '}'
  ].join('\n');

  function boot() {
    var roots = document.querySelectorAll('[data-sleeve-designer]');
    for (var i = 0; i < roots.length; i++) {
      // shopify:section:load fires on EVERY setting change in the theme editor.
      // Without this guard each firing built another instance on the same root,
      // each with its own listeners and its own 3D renderer.
      if (roots[i].__sdInit) continue;
      roots[i].__sdInit = true;
      try {
        new SleeveDesigner(roots[i]);
      } catch (err) {
        roots[i].__sdInit = false;
        console.error('[sleeve-designer] init failed', err);
      }
    }
  }

  // ---------------------------------------------------------------- helpers

  function parseJSONAttr(el, name, fallback) {
    var raw = el.getAttribute(name);
    if (!raw) return fallback;
    try { return JSON.parse(raw); } catch (e) {
      console.warn('[sleeve-designer] bad JSON in ' + name, e);
      return fallback;
    }
  }

  function clamp(v, lo, hi) { return Math.min(hi, Math.max(lo, v)); }

  function hasWebGL() {
    try {
      var c = document.createElement('canvas');
      return !!(window.WebGLRenderingContext &&
        (c.getContext('webgl2') || c.getContext('webgl')));
    } catch (e) { return false; }
  }

  function roundedRectPath(ctx, x, y, w, h, r) {
    r = Math.min(r, w / 2, h / 2);
    // Square corners: draw an actual rectangle rather than degenerate curves.
    if (!(r > 0.5)) {
      ctx.beginPath();
      ctx.rect(x, y, w, h);
      ctx.closePath();
      return;
    }
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
  }

  /** Filenames reach Shopify's file storage, so strip anything unsafe. */
  function slugify(str) {
    return String(str)
      .toLowerCase()
      .normalize ? String(str).toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40) || 'sleeve'
      : String(str).toLowerCase().replace(/[^a-z0-9]+/g, '-');
  }

  function bytesToLabel(b) {
    if (b < 1024) return b + ' B';
    if (b < 1024 * 1024) return Math.round(b / 1024) + ' KB';
    return (b / (1024 * 1024)).toFixed(1) + ' MB';
  }

  // Scale factor mapping image pixels -> canvas pixels such that the rotated
  // image fully covers the canvas.
  function coverFactor(iw, ih, cw, ch, rot) {
    var a = Math.abs(Math.cos(rot));
    var b = Math.abs(Math.sin(rot));
    var bboxW = iw * a + ih * b;
    var bboxH = iw * b + ih * a;
    return Math.max(cw / bboxW, ch / bboxH);
  }

  // ------------------------------------------------------------ constructor

  function SleeveDesigner(root) {
    this.root = root;
    root.__sd = this;
    this.sizes = parseJSONAttr(root, 'data-sizes', {});
    this.finishes = parseJSONAttr(root, 'data-finishes', {});
    // NOT `|| default`: parseFloat("0") is falsy, so `|| d` silently replaces a
    // legitimate zero with the default. That exact bug kept 3mm rounded corners
    // on after the setting was 0 -- the square-corner geometry existed but was
    // unreachable. isNaN distinguishes "absent/garbage" from "zero".
    this.borderMM = parseFloat(root.getAttribute('data-border-mm'));
    if (isNaN(this.borderMM)) this.borderMM = 1.59;
    this.maxBytes = (parseFloat(root.getAttribute('data-max-mb')) || 5) * 1024 * 1024;
    this.cornerMM = parseFloat(root.getAttribute('data-corner-mm'));
    if (isNaN(this.cornerMM)) this.cornerMM = 0;   // sleeves are cut square
    this.threeUrl = root.getAttribute('data-three-url');
    this.sizeOptionName = root.getAttribute('data-size-option') || '';
    this.finishOptionName = root.getAttribute('data-finish-option') || '';
    this.requireArtwork = root.getAttribute('data-require-artwork') === 'true';
    this.holoIntensity = parseFloat(root.getAttribute('data-holo-intensity'));
    if (isNaN(this.holoIntensity)) this.holoIntensity = 0.35;

    // Real sleeve construction, in mm. A sleeve is genuinely this thin, so at
    // true scale the edge is only perceptible when rotated toward edge-on --
    // that is accurate, not a bug. thicknessScale lets it be exaggerated if a
    // more legible mockup is wanted.
    this.frontMM = parseFloat(root.getAttribute('data-front-mm')) || 0.09;
    this.cardMM = parseFloat(root.getAttribute('data-card-mm')) || 0.10;
    this.backMM = parseFloat(root.getAttribute('data-back-mm')) || 0.12;
    this.thicknessScale = parseFloat(root.getAttribute('data-thickness-scale')) || 1;

    this.qtyOptionName = root.getAttribute('data-qty-option') || '';
    this.shortenLabels = root.getAttribute('data-short-labels') !== 'false';
    this.hideOriginal = root.getAttribute('data-hide-original') === 'true';
    this.adoptBuy = root.getAttribute('data-adopt-buy') === 'true';
    this.collapseHeading = root.getAttribute('data-collapse-heading') || '';
    this.collapseOpen = root.getAttribute('data-collapse-open') === 'true';
    this.movePaymentTerms = root.getAttribute('data-move-terms') === 'true';

    // artwork colour boost, applied to the composited print texture
    this.artSat = parseFloat(root.getAttribute('data-art-sat'));
    if (isNaN(this.artSat)) this.artSat = 1.75;
    this.artBright = parseFloat(root.getAttribute('data-art-bright'));
    if (isNaN(this.artBright)) this.artBright = 1.0;
    this.moneySample = root.getAttribute('data-money-sample') || '$1.00';
    this.variants = parseJSONAttr(root, 'data-variants', null);
    if (!this.variants) {
      var vnode = root.querySelector('[data-sd-variants-json]');
      if (vnode) { try { this.variants = JSON.parse(vnode.textContent); } catch (e) { this.variants = []; } }
    }
    this.variants = this.variants || [];

    this.sizeKeys = Object.keys(this.sizes);
    this.finishKeys = Object.keys(this.finishes);

    this.state = {
      sizeKey: this.sizeKeys[0],
      finishKey: this.finishKeys[0],
      img: null,
      file: null,
      tx: 0,          // mm offset from centre
      ty: 0,
      scale: 1,
      rot: 0          // radians
    };

    this.pointers = {};
    this.dragging = false;

    this.cacheDom();
    this.buildTextureCanvas();
    this.bindUI();
    this.syncFromVariantPicker();
    this.watchVariant();
    this.render2D();

    if (hasWebGL() && this.threeUrl) {
      this.init3D();
    } else if (!this.threeUrl) {
      this.fail3D('three.global.min.js is not in the theme assets folder (asset_url resolved to nothing).');
    } else {
      this.fail3D('WebGL is unavailable in this browser.');
    }

    // Core first, and each optional enhancement isolated. Previously a throw in
    // any one of these took out everything after it -- the pills and price
    // simply never rendered, with the tool looking half-built. Presentation
    // extras must never be able to break selection, pricing or the cart.
    var self = this;
    function step(name, fn) {
      try {
        fn.call(self);
      } catch (err) {
        console.error('[sleeve-designer] ' + name + ' failed (continuing)', err);
      }
    }

    step('syncTabs', this.syncTabs);
    step('hideOriginalPicker', this.hideOriginalPicker);
    step('adoptBuyButtons', this.adoptBuyButtons);

    // core: these must run even if anything above misbehaved
    step('buildOptionProxy', this.buildOptionProxy);
    step('updatePrice', this.updatePrice);
    step('ensureFormInputs', this.ensureFormInputs);
    step('wireAtc', this.wireAtc);
    step('updateGate', this.updateGate);
    step('bindBuyGuard', this.bindBuyGuard);
    step('alignPriceToButtons', this.alignPriceToButtons);
    var self2 = this;
    setTimeout(function () { self2.alignPriceToButtons(); }, 800);
    setTimeout(function () { self2.alignPriceToButtons(); }, 2500);

    // purely presentational, deliberately last
    step('collapseDescriptionSection', this.collapseDescriptionSection);
    step('adoptPaymentTerms', this.adoptPaymentTerms);
  }

  SleeveDesigner.prototype.cacheDom = function () {
    var r = this.root;
    var q = function (sel) { return r.querySelector(sel); };
    this.el = {
      stage3d: q('[data-sd-stage]'),
      view: q('[data-sd-view]'),
      fileInput: q('[data-sd-file]'),
      dropzone: q('[data-sd-dropzone]'),
      zoom: q('[data-sd-zoom]'),
      rotate: q('[data-sd-rotate]'),
      reset: q('[data-sd-reset]'),
      remove: q('[data-sd-remove]'),
      status: q('[data-sd-status]'),
      dpi: q('[data-sd-dpi]'),
      filename: q('[data-sd-filename]'),
      controls: q('[data-sd-controls]'),
      tabs: r.querySelectorAll('[data-sd-tab]'),
      panes: r.querySelectorAll('[data-sd-pane]'),
      spinBtn: q('[data-sd-spin]'),
      sizeLabel: q('[data-sd-size-label]'),
      finishLabel: q('[data-sd-finish-label]'),
      optionsPrimary: q('[data-sd-options="primary"]'),
      optionsSecondary: q('[data-sd-options="secondary"]'),
      priceNow: q('[data-sd-price-now]'),
      priceWas: q('[data-sd-price-was]'),
      priceUnit: q('[data-sd-price-unit]'),
      atc: q('[data-sd-atc]'),
      buySlot: q('[data-sd-buy-slot]'),
      canvasWrap: q('.sd-canvas-wrap')
    };
  };

  // ------------------------------------------------------------- 2D texture

  SleeveDesigner.prototype.size = function () {
    return this.sizes[this.state.sizeKey] || { w: 66, h: 91 };
  };

  SleeveDesigner.prototype.finish = function () {
    return this.finishes[this.state.finishKey] ||
      { finish: 'gloss', border: 'black', base: 'black' };
  };

  SleeveDesigner.prototype.buildTextureCanvas = function () {
    this.texCanvas = document.createElement('canvas');
    this.texCtx = this.texCanvas.getContext('2d');
    // The 3D print renders from a filtered COPY, so the saturation/brightness
    // boost applies to the preview without altering the 2D editor, the
    // customer's file, or what the designer receives.
    this.fxCanvas = document.createElement('canvas');
    this.fxCtx = this.fxCanvas.getContext('2d');
  };

  SleeveDesigner.prototype.updateFxCanvas = function () {
    var src = this.texCanvas, dst = this.fxCanvas, ctx = this.fxCtx;
    if (dst.width !== src.width || dst.height !== src.height) {
      dst.width = src.width;
      dst.height = src.height;
      // The GPU texture was allocated at the OLD canvas size. three.js uploads
      // resized canvases with texSubImage2D into that stale allocation, which
      // squeezed the print into a corner of the sleeve and showed leftover
      // pixels along two edges (the "Japanese size border" bug — any size
      // switch, either direction). dispose() drops the allocation so the next
      // upload re-creates the texture at the new dimensions.
      if (this.tex) this.tex.dispose();
    }
    ctx.clearRect(0, 0, dst.width, dst.height);
    var f = 'saturate(' + this.artSat + ') brightness(' + this.artBright + ')';
    try { ctx.filter = f; } catch (e) { /* very old browsers: unfiltered copy */ }
    ctx.drawImage(src, 0, 0);
    ctx.filter = 'none';
  };

  SleeveDesigner.prototype.baseColorHex = function () {
    var f = this.finish();
    var base = f.base || f.border || 'black';
    return base === 'white' ? '#f4f4f2' : '#131313';
  };

  /**
   * Draws the printed back face into this.texCanvas.
   * Order: base film -> artwork (transformed) -> border frame on top.
   * The border deliberately overlays the artwork, matching how it is printed.
   */
  SleeveDesigner.prototype.drawTexture = function () {
    var s = this.size();
    var cw = Math.round(s.w * PX_PER_MM);
    var ch = Math.round(s.h * PX_PER_MM);
    if (this.texCanvas.width !== cw) this.texCanvas.width = cw;
    if (this.texCanvas.height !== ch) this.texCanvas.height = ch;

    var ctx = this.texCtx;
    var f = this.finish();
    ctx.clearRect(0, 0, cw, ch);

    // base film
    ctx.fillStyle = this.baseColorHex();
    ctx.fillRect(0, 0, cw, ch);

    // artwork
    var st = this.state;
    if (st.img) {
      var iw = st.img.naturalWidth || st.img.width;
      var ih = st.img.naturalHeight || st.img.height;
      var k = coverFactor(iw, ih, cw, ch, st.rot) * st.scale;
      ctx.save();
      ctx.translate(cw / 2 + st.tx * PX_PER_MM, ch / 2 + st.ty * PX_PER_MM);
      ctx.rotate(st.rot);
      ctx.drawImage(st.img, -iw * k / 2, -ih * k / 2, iw * k, ih * k);
      ctx.restore();
      this.lastCover = k;
    } else {
      this.lastCover = null;
    }

    // border frame, drawn last so it sits over the artwork
    if (f.border) {
      var bw = this.borderMM * PX_PER_MM;
      ctx.fillStyle = f.border === 'white' ? '#f7f7f5' : '#0c0c0c';
      ctx.beginPath();
      ctx.rect(0, 0, cw, ch);
      ctx.rect(bw, bw, cw - 2 * bw, ch - 2 * bw);
      ctx.fill('evenodd');
    }

    if (this.tex) {
      this.updateFxCanvas();
      this.tex.needsUpdate = true;
      // The render loop only draws when asked. Without this, a texture change
      // with auto-rotate stopped (e.g. Remove image after a drag) leaves the
      // canvas showing the LAST frame -- stale artwork included.
      this.needsRender = true;
    }
  };

  /** Visible editor canvas: the texture, clipped to the sleeve outline, plus guides. */
  SleeveDesigner.prototype.render2D = function () {
    this.drawTexture();

    var view = this.el.view;
    if (!view) return;
    var s = this.size();
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    var boxW = view.clientWidth || 320;
    var boxH = Math.round(boxW * (s.h / s.w));

    if (view.width !== Math.round(boxW * dpr) || view.height !== Math.round(boxH * dpr)) {
      view.width = Math.round(boxW * dpr);
      view.height = Math.round(boxH * dpr);
    }
    view.style.height = boxH + 'px';

    var ctx = view.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, boxW, boxH);

    var pxPerMM = boxW / s.w;
    var radius = this.cornerMM * pxPerMM;

    ctx.save();
    roundedRectPath(ctx, 0, 0, boxW, boxH, radius);
    ctx.clip();
    ctx.drawImage(this.texCanvas, 0, 0, boxW, boxH);
    ctx.restore();

    // guides: sleeve outline + border coverage line
    ctx.save();
    ctx.lineWidth = 1;
    ctx.strokeStyle = 'rgba(255,255,255,0.55)';
    roundedRectPath(ctx, 0.5, 0.5, boxW - 1, boxH - 1, radius);
    ctx.stroke();

    var f = this.finish();
    if (f.border) {
      var b = this.borderMM * pxPerMM;
      ctx.setLineDash([5, 4]);
      ctx.strokeStyle = f.border === 'white'
        ? 'rgba(20,20,20,0.75)' : 'rgba(255,255,255,0.75)';
      ctx.strokeRect(b, b, boxW - 2 * b, boxH - 2 * b);
    }
    ctx.restore();

    this.updateReadouts();
    this.updateNote();      // synchronous with every state change
  };

  /** Keeps properties[_design] in step with the current state, every frame. */
  SleeveDesigner.prototype.updateNote = function () {
    var form = this.form || this.findForm();
    if (!form) return;
    var note = form.querySelector('[data-sd-prop="note"]');
    if (!note) return;
    note.value = this.state.file ? this.designNote() : '';
  };

  SleeveDesigner.prototype.updateReadouts = function () {
    var s = this.size();
    var f = this.finish();
    if (this.el.sizeLabel) {
      this.el.sizeLabel.textContent = this.state.sizeKey + ' — ' + s.w + ' x ' + s.h + ' mm';
    }
    if (this.el.finishLabel) {
      this.el.finishLabel.textContent = f.border
        ? f.finish + ', ' + f.border + ' border'
        : f.finish + ', borderless';
    }
    if (this.el.dpi) {
      if (this.lastCover) {
        var ppi = (MM_PER_INCH * PX_PER_MM) / this.lastCover;
        var low = ppi < LOW_DPI_THRESHOLD;
        this.el.dpi.textContent = Math.round(ppi) + ' PPI at this size';
        this.el.dpi.classList.toggle('sd-warn', low);
        this.el.dpi.hidden = false;
      } else {
        this.el.dpi.hidden = true;
      }
    }
  };

  // ---------------------------------------------------------------- upload

  SleeveDesigner.prototype.loadFile = function (file) {
    var self = this;
    if (!file) return;
    if (!/^image\//.test(file.type)) {
      return this.say('That file is not an image. Please upload a PNG or JPG.', true);
    }
    if (file.size > this.maxBytes) {
      return this.say('That file is ' + bytesToLabel(file.size) + '. Please keep it under ' +
        bytesToLabel(this.maxBytes) + '.', true);
    }

    var url = URL.createObjectURL(file);
    var img = new Image();
    img.onload = function () {
      // Downscale oversized images for the GPU texture. The customer's original
      // file is what goes to the cart, untouched.
      var MAXDIM = 2048;
      var big = Math.max(img.naturalWidth, img.naturalHeight);
      if (big > MAXDIM) {
        var r = MAXDIM / big;
        var c = document.createElement('canvas');
        c.width = Math.round(img.naturalWidth * r);
        c.height = Math.round(img.naturalHeight * r);
        c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
        var small = new Image();
        small.onload = function () { URL.revokeObjectURL(url); self.adopt(small, file); };
        small.src = c.toDataURL('image/png');
      } else {
        self.adopt(img, file);
      }
    };
    img.onerror = function () {
      URL.revokeObjectURL(url);
      self.say('That image could not be read. Try re-saving it as a PNG or JPG.', true);
    };
    img.src = url;
  };

  SleeveDesigner.prototype.adopt = function (img, file) {
    this.state.img = img;
    this.state.file = file;
    this.state.tx = 0; this.state.ty = 0; this.state.scale = 1; this.state.rot = 0;
    if (this.el.zoom) this.el.zoom.value = '100';
    if (this.el.rotate) this.el.rotate.value = '0';
    if (this.el.filename) this.el.filename.textContent = file.name + ' — ' + bytesToLabel(file.size);
    this.root.classList.add('sd--has-art');
    this.say('');

    // Auto-rotate kicks in once artwork exists, so the customer sees the
    // sleeve as an object without having to discover dragging. Any pointer
    // interaction with the 3D stops it (bindOrbit). Respects reduced-motion.
    if (this.renderer &&
        !(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches)) {
      this.spin = true;
      if (this.el.spinBtn) this.el.spinBtn.setAttribute('aria-pressed', 'true');
    }

    this.render2D();
    // (constructor runs the full step sequence; here only the form wiring
    // and gate need refreshing)
    this.ensureFormInputs();
    this.updateGate();
    this.updatePrice();
  };

  SleeveDesigner.prototype.clearArt = function () {
    this.state.img = null;
    this.state.file = null;
    this.root.classList.remove('sd--has-art');
    if (this.el.filename) this.el.filename.textContent = '';
    if (this.el.fileInput) this.el.fileInput.value = '';
    this.render2D();
    // (constructor runs the full step sequence; here only the form wiring
    // and gate need refreshing)
    this.ensureFormInputs();
    this.updateGate();
    this.updatePrice();
  };

  SleeveDesigner.prototype.say = function (msg, isError) {
    if (!this.el.status) return;
    this.el.status.textContent = msg || '';
    this.el.status.classList.toggle('sd-warn', !!isError);
    this.el.status.hidden = !msg;
  };

  // ------------------------------------------------------------ interaction

  SleeveDesigner.prototype.bindUI = function () {
    var self = this;
    var el = this.el;

    if (el.fileInput) {
      el.fileInput.addEventListener('change', function () {
        self.loadFile(this.files && this.files[0]);
      });
    }

    if (el.dropzone) {
      ['dragenter', 'dragover'].forEach(function (evt) {
        el.dropzone.addEventListener(evt, function (e) {
          e.preventDefault(); el.dropzone.classList.add('sd-drop-hot');
        });
      });
      ['dragleave', 'drop'].forEach(function (evt) {
        el.dropzone.addEventListener(evt, function (e) {
          e.preventDefault(); el.dropzone.classList.remove('sd-drop-hot');
        });
      });
      el.dropzone.addEventListener('drop', function (e) {
        if (e.dataTransfer && e.dataTransfer.files) self.loadFile(e.dataTransfer.files[0]);
      });
    }

    if (el.zoom) {
      el.zoom.addEventListener('input', function () {
        self.state.scale = parseFloat(this.value) / 100;
        self.render2D();
      });
    }
    if (el.rotate) {
      el.rotate.addEventListener('input', function () {
        self.state.rot = parseFloat(this.value) * Math.PI / 180;
        self.render2D();
      });
    }
    if (el.reset) {
      el.reset.addEventListener('click', function () {
        self.state.tx = 0; self.state.ty = 0; self.state.scale = 1; self.state.rot = 0;
        if (el.zoom) el.zoom.value = '100';
        if (el.rotate) el.rotate.value = '0';
        self.render2D();
      });
    }
    if (el.remove) {
      el.remove.addEventListener('click', function () { self.clearArt(); });
    }

    // drag to reposition on the 2D editor
    if (el.view) {
      var last = null;
      el.view.addEventListener('pointerdown', function (e) {
        if (!self.state.img) return;
        el.view.setPointerCapture(e.pointerId);
        self.pointers[e.pointerId] = { x: e.clientX, y: e.clientY };
        last = { x: e.clientX, y: e.clientY };
        self.dragging = true;
      });
      el.view.addEventListener('pointermove', function (e) {
        if (!self.dragging || !self.state.img) return;
        self.pointers[e.pointerId] = { x: e.clientX, y: e.clientY };
        var ids = Object.keys(self.pointers);
        if (ids.length >= 2) {
          // pinch zoom
          var a = self.pointers[ids[0]], b = self.pointers[ids[1]];
          var d = Math.hypot(a.x - b.x, a.y - b.y);
          if (self.pinchBase) {
            var next = clamp(self.pinchScale * (d / self.pinchBase), 0.25, 4);
            self.state.scale = next;
            if (el.zoom) el.zoom.value = String(Math.round(next * 100));
          } else {
            self.pinchBase = d; self.pinchScale = self.state.scale;
          }
          self.render2D();
          return;
        }
        var s = self.size();
        var mmPerPx = s.w / (el.view.clientWidth || 320);
        self.state.tx += (e.clientX - last.x) * mmPerPx;
        self.state.ty += (e.clientY - last.y) * mmPerPx;
        self.state.tx = clamp(self.state.tx, -s.w, s.w);
        self.state.ty = clamp(self.state.ty, -s.h, s.h);
        last = { x: e.clientX, y: e.clientY };
        self.render2D();
      });
      ['pointerup', 'pointercancel', 'pointerleave'].forEach(function (evt) {
        el.view.addEventListener(evt, function (e) {
          delete self.pointers[e.pointerId];
          if (!Object.keys(self.pointers).length) {
            self.dragging = false; self.pinchBase = null;
          }
        });
      });
      el.view.addEventListener('wheel', function (e) {
        if (!self.state.img) return;
        e.preventDefault();
        var next = clamp(self.state.scale * (e.deltaY < 0 ? 1.06 : 0.94), 0.25, 4);
        self.state.scale = next;
        if (el.zoom) el.zoom.value = String(Math.round(next * 100));
        self.render2D();
      }, { passive: false });
    }

    // tabs (mobile)
    for (var i = 0; i < el.tabs.length; i++) {
      (function (btn) {
        btn.addEventListener('click', function () {
          self.setTab(btn.getAttribute('data-sd-tab'));
        });
      })(el.tabs[i]);
    }

    if (el.spinBtn) {
      el.spinBtn.addEventListener('click', function () { self.toggleSpin(); });
    }

    var ro = window.ResizeObserver ? new ResizeObserver(function () {
      self.syncTabs();
      self.render2D();
      self.resize3D();
    }) : null;
    if (ro) ro.observe(this.root);
    window.addEventListener('resize', function () { self.syncTabs(); self.alignPriceToButtons(); });
  };

  SleeveDesigner.prototype.setTab = function (name) {
    var el = this.el, i;
    this.currentTab = name;
    for (i = 0; i < el.tabs.length; i++) {
      var t = el.tabs[i];
      var on = t.getAttribute('data-sd-tab') === name;
      t.classList.toggle('sd-tab--on', on);
      t.setAttribute('aria-selected', on ? 'true' : 'false');
    }
    for (i = 0; i < el.panes.length; i++) {
      var p = el.panes[i];
      p.hidden = p.getAttribute('data-sd-pane') !== name;
    }
    if (name === 'preview') { this.resize3D(); }
    else { this.render2D(); }
  };

  /**
   * Keeps pane visibility in step with the CSS breakpoint.
   *
   * Below STACK_BREAKPOINT the panes stack and the tabs take over, so exactly
   * one pane must be visible. Without this the tabs rendered but nothing was
   * hidden until the customer clicked one -- both panes stacked and the section
   * ran ~1325px tall, defeating the no-scroll layout.
   */
  SleeveDesigner.prototype.syncTabs = function () {
    var stacked = window.matchMedia
      ? window.matchMedia('(max-width: ' + STACK_BREAKPOINT + 'px)').matches
      : window.innerWidth <= STACK_BREAKPOINT;

    if (stacked) {
      if (!this.stacked) {
        this.stacked = true;
        this.setTab(this.currentTab || 'design');
      }
    } else if (this.stacked !== false) {
      this.stacked = false;
      for (var i = 0; i < this.el.panes.length; i++) this.el.panes[i].hidden = false;
      this.render2D();
      this.resize3D();
    }
  };

  // ------------------------------------------------------------------- 3D

  /**
   * Loads three.js as a CLASSIC script exposing window.THREE.
   *
   * This was a dynamic import() of the ESM build and it FAILED IN PRODUCTION
   * with "require is not defined" -- while importing fine from the console on
   * the same page. three.js itself never calls require(); the only matches in
   * the file are inside strings. Something else on the storefront (an app
   * bundler runtime -- LayoutHub, Afterpay and others are present) patches
   * dynamic import(). A plain <script> tag has none of that surface and cannot
   * be intercepted.
   *
   * The section also emits a deferred <script> for the global build, so
   * window.THREE is normally already there and this never has to fetch.
   */
  function loadThree(url, cb) {
    if (window.THREE) return cb(window.THREE);

    var existing = document.querySelector('script[data-sd-three]');
    if (existing) {
      existing.addEventListener('load', function () { cb(window.THREE || null, 'script loaded but no global'); });
      existing.addEventListener('error', function () { cb(null, 'script tag failed'); });
      return;
    }
    if (!url) return cb(null, 'empty asset url');

    var sc = document.createElement('script');
    sc.src = url;
    sc.async = true;
    sc.setAttribute('data-sd-three', '');
    sc.onload = function () { cb(window.THREE || null, window.THREE ? null : 'script loaded but no global'); };
    sc.onerror = function () {
      // last resort: some environments block injected scripts but allow modules
      try {
        import(url).then(function (m) { cb(m || null, null); })
                   .catch(function (e) { cb(null, 'script tag AND import failed: ' + (e && e.message)); });
      } catch (e) {
        cb(null, 'script tag failed and import unavailable');
      }
    };
    document.head.appendChild(sc);
  }

  SleeveDesigner.prototype.init3D = function () {
    var self = this;
    loadThree(this.threeUrl, function (THREE, why) {
      if (!THREE) {
        return self.fail3D(
          'three.js did not load (' + (why || 'unknown') + '). Resolved URL: ' +
          (self.threeUrl || '(EMPTY - is three.global.min.js in the theme assets folder?)')
        );
      }
      self.THREE = THREE;
      try {
        self.build3D();
      } catch (err) {
        console.error('[sleeve-designer] 3D scene failed to build', err);
        self.fail3D('3D scene failed to build: ' + (err && err.message));
      }
    });
  };

  /** Visible, explained degradation -- never a silently missing feature. */
  SleeveDesigner.prototype.fail3D = function (detail) {
    console.error('[sleeve-designer] ' + detail);
    this.root.classList.add('sd--3d-off');
    var stage = this.el.stage3d;
    if (stage) {
      stage.textContent = '';
      var p = document.createElement('p');
      p.className = 'sd-3d-error';
      p.textContent = '3D preview is not available in this browser. Your design ' +
        'and the placement above are still saved with your order.';
      stage.appendChild(p);
    }
    var spin = this.el.spinBtn;
    if (spin) spin.hidden = true;
  };

  SleeveDesigner.prototype.build3D = function () {
    var THREE = this.THREE;
    var stage = this.el.stage3d;
    if (!stage) return;

    var renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    // ACES keeps the holographic highlights from blowing out, but it also
    // darkens and desaturates the artwork. Exposure is lifted here and the
    // print carries an emissiveMap (see buildSleeveMeshes) so the customer's
    // colours survive the lighting pipeline rather than being dimmed by it.
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.04;
    stage.appendChild(renderer.domElement);
    this.renderer = renderer;

    var scene = new THREE.Scene();
    this.scene = scene;

    // near/far are deliberately tight around the sleeve. The printed face sits
    // 0.0015mm off the film it decorates, and a 1..2000 depth range does not
    // have the precision to keep them apart.
    var camera = new THREE.PerspectiveCamera(28, 1, 80, 700);
    camera.position.set(0, 0, 330);
    this.camera = camera;

    // Procedural studio environment: a vertical gradient equirect, prefiltered.
    // Gives gloss and iridescence something real to reflect without shipping an HDR.
    var envCanvas = document.createElement('canvas');
    envCanvas.width = 32; envCanvas.height = 128;
    var ectx = envCanvas.getContext('2d');
    var grad = ectx.createLinearGradient(0, 0, 0, 128);
    grad.addColorStop(0.00, '#ffffff');
    grad.addColorStop(0.40, '#dbe3ee');
    grad.addColorStop(0.52, '#7f8798');
    grad.addColorStop(1.00, '#20242c');
    ectx.fillStyle = grad; ectx.fillRect(0, 0, 32, 128);
    var envTex = new THREE.CanvasTexture(envCanvas);
    envTex.mapping = THREE.EquirectangularReflectionMapping;
    var pmrem = new THREE.PMREMGenerator(renderer);
    scene.environment = pmrem.fromEquirectangular(envTex).texture;
    envTex.dispose();

    // A second environment just for gloss: a bright softbox band on a dark
    // studio, so glossy sleeves carry a visible light reflection that sweeps
    // across the surface as they rotate.
    var glossCanvas = document.createElement('canvas');
    glossCanvas.width = 256; glossCanvas.height = 128;
    var gctx = glossCanvas.getContext('2d');
    gctx.fillStyle = '#23262e';
    gctx.fillRect(0, 0, 256, 128);
    var band = gctx.createLinearGradient(0, 20, 0, 78);
    band.addColorStop(0, 'rgba(255,255,255,0)');
    band.addColorStop(0.5, 'rgba(255,255,255,0.95)');
    band.addColorStop(1, 'rgba(255,255,255,0)');
    gctx.fillStyle = band;
    gctx.save();
    gctx.translate(128, 48); gctx.rotate(-0.18);
    gctx.fillRect(-140, -30, 280, 60);
    gctx.restore();
    var glossTex = new THREE.CanvasTexture(glossCanvas);
    glossTex.mapping = THREE.EquirectangularReflectionMapping;
    this.envGloss = pmrem.fromEquirectangular(glossTex).texture;
    glossTex.dispose();
    pmrem.dispose();

    scene.add(new THREE.HemisphereLight(0xffffff, 0x404450, 0.55));
    var key = new THREE.DirectionalLight(0xffffff, 2.0);
    key.position.set(-120, 180, 240); scene.add(key);
    var fill = new THREE.DirectionalLight(0xdfe8ff, 0.7);
    fill.position.set(180, -60, 160); scene.add(fill);
    var rim = new THREE.DirectionalLight(0xffffff, 1.1);
    rim.position.set(60, 40, -220); scene.add(rim);

    this.group = new THREE.Group();
    scene.add(this.group);

    this.updateFxCanvas();
    this.tex = new THREE.CanvasTexture(this.fxCanvas);
    this.tex.colorSpace = THREE.SRGBColorSpace;
    this.tex.anisotropy = renderer.capabilities.getMaxAnisotropy();

    this.buildSleeveMeshes();
    this.applyFinishMaterial();

    // opening pose: printed back toward the viewer, slight tilt
    this.group.rotation.y = -0.26;
    this.group.rotation.x = 0.07;

    this.bindOrbit();
    this.resize3D();
    this.startLoop();
  };

  SleeveDesigner.prototype.roundedShape = function (w, h, r) {
    var THREE = this.THREE;
    var s = new THREE.Shape();
    var x = -w / 2, y = -h / 2;
    r = Math.min(r, w / 2, h / 2);

    // Card sleeves have square corners. With r at 0 the quadratic curves below
    // collapse to zero-length control segments, which extrudes fine but leaves
    // needless vertices -- emit a clean rectangle instead.
    if (!(r > 0.01)) {
      s.moveTo(x, y);
      s.lineTo(x + w, y);
      s.lineTo(x + w, y + h);
      s.lineTo(x, y + h);
      s.lineTo(x, y);
      return s;
    }

    s.moveTo(x + r, y);
    s.lineTo(x + w - r, y);
    s.quadraticCurveTo(x + w, y, x + w, y + r);
    s.lineTo(x + w, y + h - r);
    s.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    s.lineTo(x + r, y + h);
    s.quadraticCurveTo(x, y + h, x, y + h - r);
    s.lineTo(x, y + r);
    s.quadraticCurveTo(x, y, x + r, y);
    return s;
  };

  SleeveDesigner.prototype.buildSleeveMeshes = function () {
    var THREE = this.THREE;
    var g = this.group;

    while (g.children.length) {
      var c = g.children.pop();
      if (c.geometry) c.geometry.dispose();
    }

    var s = this.size();
    var w = s.w, h = s.h, r = this.cornerMM;
    var shape = this.roundedShape(w, h, r);

    // ---- real layered construction, in mm ------------------------------
    //   front film  0.09  transparent
    //   card cavity 0.10  (the card itself is not drawn -- you see the back
    //                      film's inner face through the clear front)
    //   back film   0.12  opaque, printed on its OUTER surface
    //   total       0.31
    //
    // No bevels here: at 0.12mm a bevel is both invisible and the exact thing
    // that buried the printed face last time, since a bevelled extrusion spans
    // depth + 2*bevelThickness rather than depth. Rounded corners come from the
    // shape outline instead.
    var k = this.thicknessScale;
    var FRONT_D = this.frontMM * k;
    var CARD_D = this.cardMM * k;
    var BACK_D = this.backMM * k;
    var TOTAL = FRONT_D + CARD_D + BACK_D;
    var halfT = TOTAL / 2;

    this.sleeveThickness = TOTAL;

    // opaque back film. Its inner (-Z) face is what shows through the front.
    var backGeo = new THREE.ExtrudeGeometry(shape, {
      depth: BACK_D, bevelEnabled: false, curveSegments: 16
    });
    backGeo.translate(0, 0, halfT - BACK_D);
    this.baseMat = new THREE.MeshPhysicalMaterial({
      color: new THREE.Color(this.baseColorHex()),
      roughness: 0.55, metalness: 0.0, clearcoat: 0.3, clearcoatRoughness: 0.4
    });
    g.add(new THREE.Mesh(backGeo, this.baseMat));

    // the print, on the outer surface of the back film. polygonOffset keeps it
    // from z-fighting with the film it sits on.
    var artGeo = new THREE.ShapeGeometry(shape, 16);
    var pos = artGeo.attributes.position, uv = artGeo.attributes.uv;
    for (var i = 0; i < pos.count; i++) {
      uv.setXY(i, (pos.getX(i) + w / 2) / w, (pos.getY(i) + h / 2) / h);
    }
    uv.needsUpdate = true;
    artGeo.translate(0, 0, halfT + 0.0015);
    // emissiveMap adds the artwork's own colour independent of lighting, so the
    // 3D print reads at the same brightness as the flat editor. Without it the
    // sleeve looked noticeably darker than the image the customer uploaded.
    this.artMat = new THREE.MeshPhysicalMaterial({
      map: this.tex,
      emissive: 0xffffff,
      emissiveMap: this.tex,
      emissiveIntensity: 0.20,
      polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2
    });
    this.artMesh = new THREE.Mesh(artGeo, this.artMat);
    g.add(this.artMesh);

    // holographic sheen, coplanar with the print, additively blended
    var holoGeo = artGeo.clone();
    holoGeo.translate(0, 0, 0.0008);
    this.holoMat = new THREE.ShaderMaterial({
      uniforms: { uIntensity: { value: this.holoIntensity } },
      vertexShader: HOLO_VERT,
      fragmentShader: HOLO_FRAG,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      polygonOffset: true, polygonOffsetFactor: -4, polygonOffsetUnits: -4
    });
    this.holoMesh = new THREE.Mesh(holoGeo, this.holoMat);
    this.holoMesh.visible = false;
    g.add(this.holoMesh);

    // transparent front film
    var frontGeo = new THREE.ExtrudeGeometry(shape, {
      depth: FRONT_D, bevelEnabled: false, curveSegments: 16
    });
    frontGeo.translate(0, 0, -halfT);
    this.frontMat = new THREE.MeshPhysicalMaterial({
      color: 0xffffff, transparent: true, opacity: 0.13,
      roughness: 0.03, metalness: 0.0,
      reflectivity: 0.9,
      clearcoat: 1.0, clearcoatRoughness: 0.01,
      depthWrite: false, side: THREE.DoubleSide
    });
    g.add(new THREE.Mesh(frontGeo, this.frontMat));
  };

  SleeveDesigner.prototype.applyFinishMaterial = function () {
    if (!this.artMat) return;
    var f = this.finish();
    var m = this.artMat;

    m.iridescence = 0;
    m.clearcoat = 0;
    m.sheen = 0;

    m.emissiveIntensity = 0.20;

    if (f.finish === 'matte') {
      m.roughness = 0.78; m.metalness = 0.0;
      m.clearcoat = 0.05; m.clearcoatRoughness = 0.9;
      m.emissiveIntensity = 0.22;   // matte scatters more; keep it from dulling
    } else if (f.finish === 'holo') {
      m.roughness = 0.14; m.metalness = 0.10;
      m.clearcoat = 1.0; m.clearcoatRoughness = 0.03;
      m.iridescence = 0.55;
      m.iridescenceIOR = 1.35;
      m.iridescenceThicknessRange = [180, 720];
      // holo prints on a dark film: pull the self-illumination down so the
      // preview reads darker than the gloss finishes, per merchant feedback
      m.emissiveIntensity = 0.12;
    } else { // gloss
      m.roughness = 0.09; m.metalness = 0.0;
      m.clearcoat = 1.0; m.clearcoatRoughness = 0.05;
    }

    // gloss finishes get the softbox reflection; others use the studio env
    if (this.envGloss) {
      var glossy = f.finish === 'gloss';
      m.envMap = glossy ? this.envGloss : null;
      m.envMapIntensity = glossy ? 0.85 : 1.0;
    }
    m.needsUpdate = true;

    if (this.holoMesh) this.holoMesh.visible = (f.finish === 'holo');

    if (this.baseMat) {
      this.baseMat.color.set(this.baseColorHex());
      this.baseMat.roughness = f.finish === 'matte' ? 0.8 : 0.5;
      this.baseMat.needsUpdate = true;
    }
  };

  SleeveDesigner.prototype.bindOrbit = function () {
    var self = this;
    var dom = this.renderer.domElement;
    var drag = false, last = null;
    var pointers = {};                 // active pointers, for pinch zoom
    var pinchDist = null;
    dom.style.touchAction = 'none';

    function pointerCount() { return Object.keys(pointers).length; }
    function pinchDistance() {
      var ids = Object.keys(pointers);
      var a = pointers[ids[0]], b = pointers[ids[1]];
      return Math.hypot(a.x - b.x, a.y - b.y);
    }

    dom.addEventListener('pointerdown', function (e) {
      pointers[e.pointerId] = { x: e.clientX, y: e.clientY };
      drag = pointerCount() === 1;
      if (pointerCount() === 2) { drag = false; pinchDist = pinchDistance(); }
      last = { x: e.clientX, y: e.clientY };
      self.spin = false;
      dom.setPointerCapture(e.pointerId);
    });
    dom.addEventListener('pointermove', function (e) {
      if (pointers[e.pointerId]) pointers[e.pointerId] = { x: e.clientX, y: e.clientY };
      if (pointerCount() === 2 && pinchDist) {
        var nd = pinchDistance();
        if (nd > 0) { self.nudgeZoom3D(nd / pinchDist); pinchDist = nd; }
        return;
      }
      if (!drag) return;
      var dx = e.clientX - last.x, dy = e.clientY - last.y;
      self.group.rotation.y += dx * 0.008;
      self.group.rotation.x = clamp(self.group.rotation.x + dy * 0.006, -0.9, 0.9);
      last = { x: e.clientX, y: e.clientY };
      self.needsRender = true;
    });
    ['pointerup', 'pointercancel', 'pointerleave'].forEach(function (evt) {
      dom.addEventListener(evt, function (e) {
        delete pointers[e.pointerId];
        if (pointerCount() < 2) pinchDist = null;
        if (pointerCount() === 0) drag = false;
      });
    });

    // scroll-wheel zoom, like the 2D editor's
    dom.addEventListener('wheel', function (e) {
      e.preventDefault();
      self.nudgeZoom3D(e.deltaY < 0 ? 1.08 : 1 / 1.08);
    }, { passive: false });

    // optional +/- buttons over the stage
    var zin = this.root.querySelector('[data-sd-zoom3d-in]');
    var zout = this.root.querySelector('[data-sd-zoom3d-out]');
    if (zin) zin.addEventListener('click', function () { self.nudgeZoom3D(1.2); });
    if (zout) zout.addEventListener('click', function () { self.nudgeZoom3D(1 / 1.2); });
  };

  SleeveDesigner.prototype.toggleSpin = function () {
    this.spin = !this.spin;
    if (this.el.spinBtn) {
      this.el.spinBtn.setAttribute('aria-pressed', this.spin ? 'true' : 'false');
    }
  };

  SleeveDesigner.prototype.resize3D = function () {
    if (!this.renderer || !this.el.stage3d) return;
    var w = this.el.stage3d.clientWidth || 320;
    var h = this.el.stage3d.clientHeight || Math.round(w * 1.1);
    if (!w || !h) return;
    this.renderer.setSize(w, h, false);
    var aspect = w / h;
    this.camera.aspect = aspect;
    // Fit the sleeve to whichever axis is tighter. Visible height at distance d
    // is 2*d*tan(fov/2); visible width is that times the aspect ratio.
    var s = this.size();
    var need = Math.max(s.h, s.w / aspect) * 1.23;   // 10% smaller model in the taller stage
    this.baseCamZ = need / (2 * Math.tan(this.camera.fov * Math.PI / 360));
    this.applyZoom3D();
    this.camera.updateProjectionMatrix();
    this.needsRender = true;
  };

  /** 3D preview zoom: 1 = fitted, >1 dollies the camera in. */
  SleeveDesigner.prototype.applyZoom3D = function () {
    if (!this.camera || !this.baseCamZ) return;
    this.zoom3D = clamp(this.zoom3D || 1, 0.6, 2.6);
    this.camera.position.z = this.baseCamZ / this.zoom3D;
    this.needsRender = true;
  };

  SleeveDesigner.prototype.nudgeZoom3D = function (factor) {
    this.zoom3D = clamp((this.zoom3D || 1) * factor, 0.6, 2.6);
    this.applyZoom3D();
  };

  SleeveDesigner.prototype.startLoop = function () {
    var self = this;
    this.needsRender = true;
    function frame() {
      if (self.spin) {
        self.group.rotation.y += 0.006;
        self.needsRender = true;
      }
      if (self.needsRender) {
        self.renderer.render(self.scene, self.camera);
        self.needsRender = false;
      }
      self.raf = requestAnimationFrame(frame);
    }
    frame();
  };

  // ------------------------------------------------------- variant plumbing

  SleeveDesigner.prototype.findForm = function () {
    // Related products and quick-add drawers render their own /cart/add forms,
    // so an unscoped querySelector can grab the wrong one. Prefer a form inside
    // the main product wrapper.
    var forms = document.querySelectorAll('form[action*="/cart/add"]');
    var fallback = null;
    for (var i = 0; i < forms.length; i++) {
      var f = forms[i];
      if (!f.querySelector('[name="id"]')) continue;
      if (!fallback) fallback = f;
      if (f.closest('.main-product-view, #MainProduct, [data-section-type="main-product"]')) {
        return f;
      }
    }
    return fallback;
  };

  /**
   * Finds the REAL controls for one product option.
   *
   * Metro names them differently depending on picker type, which is a trap:
   *   dropdown / select picker -> name="options[Card Size]"
   *   button / swatch picker   -> name="Card Size"        <- no options[] wrapper
   * Only checking for the first form meant reading nothing at all on a product
   * using pill swatches, silently falling back to each option's first value.
   */
  /**
   * The element option controls must live inside for Shopify to honour them.
   *
   * A page can hold several sets of identically-named option radios: the main
   * product, a quick-view drawer, a featured-product section. Querying the whole
   * document produced duplicate pills -- and worse, proxying a click to a radio
   * outside <product-info> means product-info.js discards it and the pill does
   * nothing at all.
   */
  SleeveDesigner.prototype.productScope = function () {
    var form = this.findForm();
    var scope = form && form.closest
      ? form.closest('product-info, .main-product-view, #MainProduct')
      : null;
    if (!scope) {
      scope = document.querySelector('product-info') ||
              document.querySelector('.main-product-view, #MainProduct');
    }
    return scope || document;
  };

  SleeveDesigner.prototype.realControlsFor = function (name) {
    var esc = String(name).replace(/"/g, '\\"');
    var sel = 'input[type="radio"][name="' + esc + '"],' +
              'input[type="radio"][name="options[' + esc + ']"],' +
              'select[name="options[' + esc + ']"],' +
              'select[name="' + esc + '"]';
    return this.productScope().querySelectorAll(sel);
  };

  /** Current option values + availability, read from the real controls. */
  SleeveDesigner.prototype.readRealOptions = function () {
    var names = this.optionNames || [];
    var out = [];
    for (var i = 0; i < names.length; i++) {
      var nodes = this.realControlsFor(names[i]);
      if (!nodes.length) continue;
      var group = { name: names[i], values: [], selected: null };
      var seen = {};                      // one entry per distinct value
      function add(value, checked, disabled) {
        var key = '@' + value;
        if (seen[key]) {
          // merge: selected anywhere wins, unavailable everywhere to disable
          if (checked) { seen[key].checked = true; group.selected = value; }
          if (!disabled) seen[key].disabled = false;
          return;
        }
        seen[key] = { value: value, checked: !!checked, disabled: !!disabled };
        group.values.push(seen[key]);
        if (checked) group.selected = value;
      }
      for (var j = 0; j < nodes.length; j++) {
        var n = nodes[j];
        if (n.tagName === 'SELECT') {
          for (var k = 0; k < n.options.length; k++) {
            var o = n.options[k];
            add(o.value, o.selected, o.disabled);
          }
        } else {
          var dis = n.disabled || n.classList.contains('variant-input-disabled') ||
                    (n.parentNode && n.parentNode.classList &&
                     n.parentNode.classList.contains('variant-disabled'));
          add(n.value, n.checked, dis);
        }
      }
      out.push(group);
    }
    return out;
  };

  SleeveDesigner.prototype.syncFromVariantPicker = function () {
    var groups = this.readRealOptions();
    var vals = {};
    for (var i = 0; i < groups.length; i++) {
      if (groups[i].selected != null) vals[groups[i].name] = groups[i].selected;
    }
    this.applyOptionValues(vals);
    return vals;
  };

  SleeveDesigner.prototype.applyOptionValues = function (vals) {
    var changed = false;
    var sizeVal = vals[this.sizeOptionName];
    var finishVal = vals[this.finishOptionName];

    if (sizeVal && this.sizes[sizeVal] && sizeVal !== this.state.sizeKey) {
      this.state.sizeKey = sizeVal; changed = true;
    }
    if (finishVal && this.finishes[finishVal] && finishVal !== this.state.finishKey) {
      this.state.finishKey = finishVal; changed = true;
    }
    if (changed) {
      if (this.group) { this.buildSleeveMeshes(); this.applyFinishMaterial(); this.resize3D(); }
      this.render2D();
    }
    return changed;
  };

  SleeveDesigner.prototype.applyVariant = function (variant) {
    if (!variant || !variant.options) return;
    var vals = {};
    // variant.options is positional; map via the picker's option names
    var names = this.optionNames || [];
    for (var i = 0; i < variant.options.length; i++) {
      if (names[i]) vals[names[i]] = variant.options[i];
    }
    if (!Object.keys(vals).length) return this.syncFromVariantPicker();
    this.applyOptionValues(vals);
  };

  SleeveDesigner.prototype.watchVariant = function () {
    var self = this;
    this.optionNames = parseJSONAttr(this.root, 'data-option-names', []);

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      try {
        subscribe(PUB_SUB_EVENTS.variantChange, function (evt) {
          var v = evt && evt.data && evt.data.variant;
          self.applyVariant(v);
          // the form is re-rendered on variant change; re-inject and re-gate
          setTimeout(function () {
            self.ensureFormInputs(); self.wireAtc(); self.updateGate();
            self.hideOriginalPicker(); self.adoptBuyButtons(); self.adoptPaymentTerms();
            self.alignPriceToButtons();
            self.refreshOptionProxy(); self.updatePrice();
          }, 0);
        });
      } catch (e) { /* fall through to DOM listeners */ }
    }

    document.addEventListener('variant:change', function (e) {
      self.applyVariant(e.detail && e.detail.variant);
      setTimeout(function () {
        self.ensureFormInputs(); self.wireAtc(); self.updateGate();
        self.refreshOptionProxy(); self.updatePrice();
      }, 0);
    });

    // belt and braces: any change in the picker
    document.addEventListener('change', function (e) {
      var t = e.target;
      if (t && t.name && t.name.indexOf('options[') === 0) {
        self.syncFromVariantPicker();
        setTimeout(function () {
          self.ensureFormInputs(); self.wireAtc(); self.updateGate();
          self.refreshOptionProxy(); self.updatePrice();
        }, 60);
      }
    });
  };


  // ------------------------------------------------- option proxy + pricing

  /**
   * Shortens an option value for a pill label.
   *
   * Only safe because these pills are a PROXY: the real radio inside
   * <product-info> keeps the full value, so cart and order data are untouched.
   * "Standard (Pokemon / MTG / One Piece / Riftbound)" as a pill label is what
   * forced the layout to ~2100px tall.
   */
  SleeveDesigner.prototype.shortLabel = function (value) {
    if (!this.shortenLabels) return value;
    var v = String(value);
    v = v.replace(/\s*\([^)]*\)/g, '');          // drop parenthetical lists
    v = v.replace(/\s*[-\u2013]\s*Borderless/i, ''); // "Matte - Borderless" -> "Matte"
    v = v.replace(/\bHolographic\b/i, 'Holo');
    v = v.replace(/\s+with\s+/i, ' / ');
    v = v.replace(/\s*Border\b/i, '');
    v = v.replace(/\s*Sleeves?\b/i, '');         // "25 Sleeves" -> "25"
    v = v.replace(/\s{2,}/g, ' ').trim();
    return v || String(value);
  };

  SleeveDesigner.prototype.shortOptionName = function (name) {
    if (!this.shortenLabels) return name;
    if (/number of sleeves|quantity|qty/i.test(name)) return 'Quantity';
    return String(name).replace(/\s*style\b/i, '').replace(/\s*size\b/i, ' size').trim();
  };

  /**
   * Renders our own pills for every product option, split across two hosts:
   *   primary   -> beside the upload window, under the positioning controls
   *                (card size, finish -- the choices that change the preview)
   *   secondary -> full width below (quantity -- the choice that changes price)
   */
  SleeveDesigner.prototype.buildOptionProxy = function () {
    var primary = this.el.optionsPrimary;
    var secondary = this.el.optionsSecondary;
    if (!primary && !secondary) return;

    var groups = this.readRealOptions();
    if (primary) { primary.textContent = ''; primary.hidden = true; }
    if (secondary) { secondary.textContent = ''; secondary.hidden = true; }
    if (!groups.length) return;

    var self = this;
    groups.forEach(function (g) {
      var isQty = self.isQtyOption(g.name);
      var host = (isQty ? secondary : primary) || secondary || primary;
      if (!host) return;

      // The finish option gets a two-row projection (finish + border) when its
      // values span both dimensions -- see finishSplit() for the rationale.
      if (g.name === self.finishOptionName && self.finishSplit()) {
        self.buildFinishRows(host);
        host.hidden = false;
        return;
      }

      var row = document.createElement('div');
      row.className = 'sd-opt';
      row.setAttribute('data-sd-opt-row', g.name);

      var lab = document.createElement('span');
      lab.className = 'sd-opt-label';
      lab.textContent = self.shortOptionName(g.name);
      row.appendChild(lab);

      var ul = document.createElement('ul');
      ul.className = 'swatches-list' + (isQty ? ' sd-qty' : '');

      g.values.forEach(function (v) {
        var li = document.createElement('li');
        li.className = 'swatches-list-item' +
          (v.checked ? ' active' : '') + (v.disabled ? ' variant-disabled' : '');
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'swatch-label';
        btn.textContent = self.shortLabel(v.value);
        btn.title = v.value;
        btn.setAttribute('data-sd-opt', g.name);
        btn.setAttribute('data-sd-val', v.value);
        if (v.disabled) btn.setAttribute('aria-disabled', 'true');
        li.appendChild(btn);
        ul.appendChild(li);
      });

      row.appendChild(ul);
      host.appendChild(row);
      host.hidden = false;
    });

    // one delegated listener on the root covers both hosts
    if (!this.root.__sdOptBound) {
      this.root.__sdOptBound = true;
      this.root.addEventListener('click', function (e) {
        var fp = e.target.closest ? e.target.closest('[data-sd-ftype]') : null;
        if (fp) {
          if (fp.getAttribute('aria-disabled') !== 'true') {
            self.chooseFinishPart(fp.getAttribute('data-sd-ftype'), null);
          }
          return;
        }
        var bp = e.target.closest ? e.target.closest('[data-sd-btype]') : null;
        if (bp) {
          if (bp.getAttribute('aria-disabled') !== 'true') {
            self.chooseFinishPart(null, bp.getAttribute('data-sd-btype'));
          }
          return;
        }
        var btn = e.target.closest ? e.target.closest('[data-sd-val]') : null;
        if (!btn || btn.getAttribute('aria-disabled') === 'true') return;
        self.chooseOption(btn.getAttribute('data-sd-opt'), btn.getAttribute('data-sd-val'));
      });
    }
  };

  // ------------------------------------------- finish/border split rows

  /**
   * The finish option encodes two decisions in one value list: surface finish
   * (gloss/matte/holo) and border (black/white/borderless). Five long pills in
   * one row read poorly, so the proxy shows two short rows instead
   * (2026-09-18). The PRODUCT still has one option: every valid pair resolves
   * to a real option value and goes through chooseOption() like any other
   * pill, so cart, pricing and order data are untouched.
   *
   * Returns null when the values do not genuinely span two dimensions -- the
   * generic one-row proxy renders instead, so reshaping the finishes in admin
   * degrades gracefully rather than breaking the picker.
   */
  SleeveDesigner.prototype.finishSplit = function () {
    var vals = Object.keys(this.finishes);
    var map = {}, fOrder = [], bOrder = [];
    for (var i = 0; i < vals.length; i++) {
      var f = this.finishes[vals[i]];
      if (!f || !f.finish) return null;
      var ft = f.finish;
      var bt = f.border || 'borderless';
      if (!map[ft]) { map[ft] = {}; fOrder.push(ft); }
      if (map[ft][bt]) return null;             // duplicate pair: not a clean split
      map[ft][bt] = vals[i];
      if (bOrder.indexOf(bt) === -1) bOrder.push(bt);
    }
    if (fOrder.length < 2 || bOrder.length < 2) return null;
    return { map: map, finishes: fOrder, borders: bOrder };
  };

  SleeveDesigner.prototype.finishTypeLabel = function (t) {
    if (t === 'holo') return 'Holo';
    return t.charAt(0).toUpperCase() + t.slice(1);
  };

  SleeveDesigner.prototype.borderTypeLabel = function (t) {
    return t.charAt(0).toUpperCase() + t.slice(1);
  };

  SleeveDesigner.prototype.buildFinishRows = function (host) {
    var split = this.finishSplit();
    if (!split) return;

    function addRow(labelText, marker) {
      var r = document.createElement('div');
      r.className = 'sd-opt';
      r.setAttribute(marker, '');
      var lab = document.createElement('span');
      lab.className = 'sd-opt-label';
      lab.textContent = labelText;
      r.appendChild(lab);
      var ul = document.createElement('ul');
      ul.className = 'swatches-list';
      r.appendChild(ul);
      host.appendChild(r);
      return ul;
    }
    function addPill(ul, label, attr, type) {
      var li = document.createElement('li');
      li.className = 'swatches-list-item';
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'swatch-label';
      btn.textContent = label;
      btn.setAttribute(attr, type);
      li.appendChild(btn);
      ul.appendChild(li);
    }

    var fUl = addRow('Finish', 'data-sd-finish-row');
    for (var i = 0; i < split.finishes.length; i++) {
      addPill(fUl, this.finishTypeLabel(split.finishes[i]), 'data-sd-ftype', split.finishes[i]);
    }

    var bUl = addRow('Border', 'data-sd-border-row');
    for (var j = 0; j < split.borders.length; j++) {
      addPill(bUl, this.borderTypeLabel(split.borders[j]), 'data-sd-btype', split.borders[j]);
    }

    // one-line explanation shown only when a finish change forces the border
    // to hop -- the reason a pill "moved on its own" must never be a mystery
    var hint = document.createElement('p');
    hint.className = 'sd-opt-hint';
    hint.setAttribute('data-sd-finish-hint', '');
    hint.hidden = true;
    host.appendChild(hint);

    this.refreshFinishRows();
  };

  /**
   * Resolves a click on either row to a real option value. Only one of
   * ftype/btype is set per call; the other half comes from the current
   * selection. When the kept border does not exist for the new finish it
   * hops to the first available one and the hint says why -- the customer
   * can never land on a combination that is not a real variant.
   */
  SleeveDesigner.prototype.chooseFinishPart = function (ftype, btype) {
    var split = this.finishSplit();
    if (!split) return;
    var cur = this.finishes[this.state.finishKey] || {};
    var f = ftype || cur.finish || split.finishes[0];
    var b = btype || (cur.finish ? (cur.border || 'borderless') : split.borders[0]);
    if (!split.map[f]) return;

    var msg = '';
    if (!split.map[f][b]) {
      if (btype) return;                        // disabled border pill: ignore
      var old = b;
      b = null;
      for (var i = 0; i < split.borders.length; i++) {
        if (split.map[f][split.borders[i]]) { b = split.borders[i]; break; }
      }
      if (!b) return;
      msg = old === 'borderless'
        ? this.finishTypeLabel(f) + ' is not available borderless — border switched to ' + this.borderTypeLabel(b) + '.'
        : this.finishTypeLabel(f) + ' is not available with a ' + this.borderTypeLabel(old).toLowerCase() + ' border — switched to ' + this.borderTypeLabel(b) + '.';
    }

    this._finishHint = msg;
    this.chooseOption(this.finishOptionName, split.map[f][b]);
    this.refreshFinishRows();
  };

  /** Mirrors selection + availability onto the two rows (and shows the hint). */
  SleeveDesigner.prototype.refreshFinishRows = function () {
    var split = this.finishSplit();
    var root = this.root;
    var fRow = root.querySelector('[data-sd-finish-row]');
    var bRow = root.querySelector('[data-sd-border-row]');
    if (!split || !fRow || !bRow) return;

    // availability straight from the real control, so sold-out values dim here
    var disabled = {};
    var groups = this.readRealOptions();
    for (var i = 0; i < groups.length; i++) {
      if (groups[i].name !== this.finishOptionName) continue;
      for (var j = 0; j < groups[i].values.length; j++) {
        if (groups[i].values[j].disabled) disabled[groups[i].values[j].value] = true;
      }
    }

    var cur = this.finishes[this.state.finishKey] || {};
    var curF = cur.finish || null;
    var curB = curF ? (cur.border || 'borderless') : null;

    var fbtns = fRow.querySelectorAll('[data-sd-ftype]');
    for (var k = 0; k < fbtns.length; k++) {
      var ft = fbtns[k].getAttribute('data-sd-ftype');
      // a finish dims only when every border for it is missing or sold out
      var any = false;
      for (var m = 0; m < split.borders.length; m++) {
        var val = split.map[ft] && split.map[ft][split.borders[m]];
        if (val && !disabled[val]) { any = true; break; }
      }
      fbtns[k].parentNode.classList.toggle('active', ft === curF);
      fbtns[k].parentNode.classList.toggle('variant-disabled', !any);
      if (!any) fbtns[k].setAttribute('aria-disabled', 'true');
      else fbtns[k].removeAttribute('aria-disabled');
    }

    var bbtns = bRow.querySelectorAll('[data-sd-btype]');
    for (var n = 0; n < bbtns.length; n++) {
      var bt = bbtns[n].getAttribute('data-sd-btype');
      var v2 = curF && split.map[curF] ? split.map[curF][bt] : null;
      var ok = !!v2 && !disabled[v2];
      bbtns[n].parentNode.classList.toggle('active', bt === curB);
      bbtns[n].parentNode.classList.toggle('variant-disabled', !ok);
      if (!ok) bbtns[n].setAttribute('aria-disabled', 'true');
      else bbtns[n].removeAttribute('aria-disabled');
    }

    var hint = root.querySelector('[data-sd-finish-hint]');
    if (hint) {
      hint.textContent = this._finishHint || '';
      hint.hidden = !this._finishHint;
    }
  };

  SleeveDesigner.prototype.isQtyOption = function (name) {
    if (this.qtyOptionName && name === this.qtyOptionName) return true;
    return /number of sleeves|quantity|qty|sleeves/i.test(name);
  };

  /**
   * Forwards a pill click to the real control.
   *
   * This is the whole reason for the proxy. product-info.js does
   * `if (!this.contains(event.target)) return;` -- it ignores any option change
   * whose event target is outside <product-info>. Moving the picker into this
   * section would render perfectly and do nothing. Clicking the real input
   * keeps the event target where Metro expects it.
   */
  SleeveDesigner.prototype.chooseOption = function (name, value) {
    var nodes = this.realControlsFor(name);
    for (var i = 0; i < nodes.length; i++) {
      var n = nodes[i];
      if (n.tagName === 'SELECT') {
        if (n.value === value) return;
        n.value = value;
        n.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }
      if (n.value === value) {
        if (n.checked) return;
        n.click();                                  // fires change from the real input
        return;
      }
    }
  };

  /** Mirrors selected/disabled state back onto the pills. */
  SleeveDesigner.prototype.refreshOptionProxy = function () {
    var root = this.root;
    var groups = this.readRealOptions();
    groups.forEach(function (g) {
      var row = root.querySelector('[data-sd-opt-row="' + String(g.name).replace(/"/g, '\\"') + '"]');
      if (!row) return;
      g.values.forEach(function (v) {
        var btn = row.querySelector('[data-sd-val="' + String(v.value).replace(/"/g, '\\"') + '"]');
        if (!btn) return;
        var li = btn.parentNode;
        li.classList.toggle('active', !!v.checked);
        li.classList.toggle('variant-disabled', !!v.disabled);
        if (v.disabled) btn.setAttribute('aria-disabled', 'true');
        else btn.removeAttribute('aria-disabled');
      });
    });
    this.refreshFinishRows();
  };

  /** Hides Metro's own picker so the page shows one set of options, not two. */
  /**
   * Belt-and-braces to the CSS rule the section emits. Scoped to the product's
   * own <product-info>: an unscoped document.querySelector('variant-selects')
   * grabs whichever comes first in the DOM, which may be a quick-view drawer's
   * picker rather than this product's -- hiding the wrong one and leaving the
   * real one on screen.
   *
   * Visually hidden only. These inputs must stay in the DOM, and inside
   * <product-info>, or variant selection breaks.
   */
  SleeveDesigner.prototype.hideOriginalPicker = function () {
    if (!this.hideOriginal) return;
    var scope = this.productScope();
    if (!scope || !scope.querySelectorAll) return;
    var nodes = scope.querySelectorAll('variant-selects, .product-block-variant-picker');
    for (var i = 0; i < nodes.length; i++) {
      if (!nodes[i].classList.contains('sd-visually-hidden')) {
        nodes[i].classList.add('sd-visually-hidden');
      }
    }
  };

  // ---- pricing -----------------------------------------------------------

  SleeveDesigner.prototype.moneyParts = function () {
    if (this._money) return this._money;
    var m = String(this.moneySample);
    var first = m.search(/[0-9]/);
    var last = m.length - 1 - String(m).split('').reverse().join('').search(/[0-9]/);
    this._money = {
      pre: first > 0 ? m.slice(0, first) : '',
      post: last < m.length - 1 ? m.slice(last + 1) : '',
      comma: /1[.,]00/.test(m) ? m.charAt(m.search(/1[.,]00/) + 1) : '.'
    };
    return this._money;
  };

  SleeveDesigner.prototype.formatCents = function (cents) {
    var mp = this.moneyParts();
    var whole = Math.floor(cents / 100);
    var frac = String(Math.round(cents % 100));
    while (frac.length < 2) frac = '0' + frac;
    var s = String(whole).replace(/\B(?=(\d{3})+(?!\d))/g, mp.comma === ',' ? '.' : ',');
    return mp.pre + s + mp.comma + frac + mp.post;
  };

  SleeveDesigner.prototype.currentVariant = function () {
    var names = this.optionNames || [];
    var groups = this.readRealOptions();
    var sel = {};
    groups.forEach(function (g) { if (g.selected != null) sel[g.name] = g.selected; });
    for (var i = 0; i < this.variants.length; i++) {
      var v = this.variants[i], ok = true;
      for (var j = 0; j < names.length; j++) {
        if (sel[names[j]] !== undefined && v.o[j] !== sel[names[j]]) { ok = false; break; }
      }
      if (ok) return v;
    }
    return null;
  };

  SleeveDesigner.prototype.selectedQty = function () {
    var groups = this.readRealOptions();
    for (var i = 0; i < groups.length; i++) {
      if (this.isQtyOption(groups[i].name) && groups[i].selected) {
        var m = /(\d[\d,\s]*)/.exec(groups[i].selected);
        if (m) return parseInt(m[1].replace(/[,\s]/g, ''), 10);
      }
    }
    return null;
  };

  SleeveDesigner.prototype.updatePrice = function () {
    if (!this.el.priceNow) return;
    var v = this.currentVariant();
    if (!v) { this.el.priceNow.textContent = ''; return; }

    this.el.priceNow.textContent = v.p;

    if (this.el.priceWas) {
      var show = v.cp && v.cp > v.c;
      this.el.priceWas.textContent = show ? this.formatCents(v.cp) : '';
      this.el.priceWas.hidden = !show;
    }

    if (this.el.priceUnit) {
      var q = this.selectedQty();
      if (q && q > 1) {
        this.el.priceUnit.textContent = this.formatCents(v.c / q) + ' per sleeve';
        this.el.priceUnit.hidden = false;
      } else {
        this.el.priceUnit.hidden = true;
      }
    }
  };

  // ---- add to cart -------------------------------------------------------

  /**
   * Associates our button with the real product form via the form="" attribute,
   * so it submits through product-form.js exactly like Metro's own button --
   * including the AJAX cart drawer and our own artwork gate.
   */
  SleeveDesigner.prototype.wireAtc = function () {
    var btn = this.el.atc;
    if (!btn) return;
    var form = this.findForm();
    if (!form) return;

    // NOT form.id -- every Shopify product form contains <input name="id"> for
    // the variant, and HTMLFormElement's legacy named-property getter exposes
    // that input as an OWN property, shadowing the inherited `id` accessor.
    // form.id therefore returns an HTMLInputElement, and setting it does
    // nothing useful. Always go through get/setAttribute on a product form.
    var formId = form.getAttribute('id');
    if (!formId) {
      formId = 'SleeveDesignerProductForm';
      form.setAttribute('id', formId);
    }
    btn.setAttribute('form', formId);

    if (btn.__sdWired) return;
    btn.__sdWired = true;
    var self = this;
    btn.addEventListener('click', function () {
      if (btn.getAttribute('aria-disabled') === 'true') return;
      btn.classList.add('sd-loading');
      setTimeout(function () { btn.classList.remove('sd-loading'); }, 5000);
    });
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      try {
        subscribe(PUB_SUB_EVENTS.cartUpdate, function () { btn.classList.remove('sd-loading'); });
        subscribe(PUB_SUB_EVENTS.cartError, function () { btn.classList.remove('sd-loading'); });
      } catch (e) { /* not fatal */ }
    }
  };

  /**
   * Relocates Metro's own buy-buttons block into the tool.
   *
   * Unlike the variant picker, this CAN be moved. Every `this.productForm`
   * reference in product-info.js is null-safe (`?.` or `?? document`), and the
   * variant re-render finds `#ProductBuyButtons-…` by id across the whole
   * document, so it keeps updating in its new home. The containment guard only
   * governs option changes, not form submission.
   */
  SleeveDesigner.prototype.adoptBuyButtons = function () {
    if (!this.adoptBuy) return;
    var slot = this.el.buySlot;
    if (!slot) return;
    var scope = this.productScope();
    var bb = (scope.querySelector && scope.querySelector('[id^="ProductBuyButtons-"]')) ||
             document.querySelector('[id^="ProductBuyButtons-"]');
    if (!bb || slot.contains(bb)) return;
    slot.appendChild(bb);
    this.root.classList.add('sd--adopted-buy');
  };

  /**
   * Turns a run of the product description into one of Metro's collapsible rows.
   *
   * The description is product data, shared with the LIVE theme, so it is NOT
   * edited: the nodes are relocated in the DOM on this template only. Editing
   * the description in admin would strip the content from the live product page
   * too, since Metro is still a draft. This way the source stays intact and the
   * row updates automatically if the description is ever changed.
   */
  SleeveDesigner.prototype.collapseDescriptionSection = function () {
    var headingText = (this.collapseHeading || '').trim();
    if (!headingText) return;

    var desc = document.querySelector('[product-description], .product-block-desc');
    if (!desc || desc.__sdCollapsed) return;

    var needle = headingText.toLowerCase().replace(/[:\s]+$/, '');
    var candidates = desc.querySelectorAll('p, h2, h3, h4, h5, h6');
    var head = null;
    for (var i = 0; i < candidates.length; i++) {
      var txt = (candidates[i].textContent || '').trim().toLowerCase().replace(/[:\s]+$/, '');
      if (txt === needle) { head = candidates[i]; break; }
    }
    if (!head) return;

    // everything up to the next paragraph or heading belongs to this section
    var body = [];
    var n = head.nextElementSibling;
    while (n && !/^(P|H[1-6])$/.test(n.tagName)) {
      body.push(n);
      n = n.nextElementSibling;
    }
    if (!body.length) return;

    var wrap = document.createElement('div');
    wrap.className = 'product-block product-collapsible sd-collapsed-section';

    var details;
    try {
      // upgrades to Metro's accordion behaviour where customized built-ins are
      // supported; falls back to a plain <details>, which still opens and closes
      details = document.createElement('details', { is: 'accordion-details' });
    } catch (e) {
      details = document.createElement('details');
    }
    details.className = 'collapsible-product-content-item';
    if (this.collapseOpen) details.setAttribute('open', '');

    var summary = document.createElement('summary');
    summary.className = 'collapsible-heading';
    var label = document.createElement('div');
    label.className = 'collapsible-heading-text text font-bold';
    label.textContent = headingText.replace(/[:\s]+$/, '');
    summary.appendChild(label);

    var icon = document.querySelector('.product-collapsible .collapsible-icon');
    if (icon) {
      summary.appendChild(icon.cloneNode(true));
    } else {
      var span = document.createElement('span');
      span.className = 'collapsible-icon';
      span.textContent = '+';
      summary.appendChild(span);
    }
    details.appendChild(summary);

    var content = document.createElement('div');
    content.className = 'collapsible-content';
    content.setAttribute('collapsible-content-wrapper', '');
    var inner = document.createElement('div');
    inner.className = 'collapsible-content-body rte';
    for (var j = 0; j < body.length; j++) inner.appendChild(body[j]);
    content.appendChild(inner);
    details.appendChild(content);
    wrap.appendChild(details);

    head.parentNode.insertBefore(wrap, head);
    head.parentNode.removeChild(head);
    desc.__sdCollapsed = true;
  };

  /**
   * Moves the Shop Pay installments banner in beneath the express checkout
   * button and above "More payment options".
   *
   * Shopify renders the payment button and its more-options link
   * asynchronously, so this watches for them rather than racing them. Placement
   * is INSIDE .shopify-payment-button, which is Shopify's own component -- if it
   * ever re-renders and drops our node, the poll re-places it.
   */
  SleeveDesigner.prototype.adoptPaymentTerms = function () {
    if (!this.movePaymentTerms) return;
    var slot = this.el.buySlot;
    if (!slot) return;

    // Hold the node across re-renders: Metro replaces ProductBuyButtons'
    // innerHTML on every variant change, which detaches anything placed inside
    // it. The reference survives, so it can simply be re-inserted.
    var terms = this._paymentTerms ||
                document.querySelector('.product-block-payment-installments');
    if (!terms) return;
    this._paymentTerms = terms;

    if (this._termsPoll) { clearInterval(this._termsPoll); this._termsPoll = null; }

    // Move it in IMMEDIATELY. Position is refined below once Shopify's express
    // button renders, but the banner must never be left sitting in the product
    // column waiting for a button that may never appear -- it renders lazily,
    // and stays hidden until artwork exists.
    if (!slot.contains(terms)) slot.appendChild(terms);

    function refine() {
      var payBtn = slot.querySelector('.shopify-payment-button');
      if (!payBtn) return false;
      var more = payBtn.querySelector('.shopify-payment-button__more-options');
      if (more) {
        // the requested spot: under Buy with Shop, above More payment options
        if (terms.parentNode !== payBtn || terms.nextElementSibling !== more) {
          payBtn.insertBefore(terms, more);
        }
        return true;
      }
      // express button present but no more-options link: sit just beneath it
      if (terms.previousElementSibling !== payBtn) {
        payBtn.parentNode.insertBefore(terms, payBtn.nextSibling);
      }
      return false;
    }

    if (refine()) return;
    var self = this;
    var tries = 0;
    this._termsPoll = setInterval(function () {
      tries += 1;
      if (refine() || tries > 40) {                   // ~10s
        clearInterval(self._termsPoll);
        self._termsPoll = null;
      }
    }, 250);
  };

  /**
   * Catches any buy attempt made before a design exists.
   *
   * Bound on .sd-buy, not on the buttons: the CSS overlay covers the whole buy
   * area, so this receives the click no matter which control was aimed at --
   * including Shopify's express checkout, which may live in an iframe that a
   * button-level listener could never observe.
   */
  SleeveDesigner.prototype.bindBuyGuard = function () {
    var buy = this.root.querySelector('.sd-buy');
    if (!buy || buy.__sdBuyGuard) return;
    buy.__sdBuyGuard = true;
    var self = this;

    buy.addEventListener('click', function (e) {
      if (!self.requireArtwork || self.state.file) return;   // all good, let it through
      e.preventDefault();
      e.stopPropagation();

      self.say('Please upload your design first — both Add to cart and express checkout need it.', true);

      var zone = self.el.dropzone;
      if (zone) {
        zone.classList.remove('sd-flash');
        void zone.offsetWidth;                                // restart the animation
        zone.classList.add('sd-flash');
        setTimeout(function () { zone.classList.remove('sd-flash'); }, 1200);
      }
      var target = self.el.canvasWrap || zone || self.el.view;
      if (target && target.scrollIntoView) {
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, true);
  };

  /**
   * Pins the price to the Add-to-cart button's actual vertical position.
   *
   * Metro's adopted buy-buttons block renders extra elements and margins ABOVE
   * the buttons (error wrapper, form chrome), so the buttons sit lower than the
   * grid row top while the price stays at it -- a gap no static CSS can predict.
   * Measure where the button really is and offset the price to match.
   */
  SleeveDesigner.prototype.alignPriceToButtons = function () {
    var price = this.root.querySelector('.sd-price');
    var slot = this.el.buySlot;
    if (!price) return;
    var btn = (slot && slot.querySelector(
      '.addtocart-button, .shopify-payment-button__button, [name="add"]'));
    if (!btn || !slot) { price.style.marginTop = ''; return; }
    var offset = Math.round(btn.getBoundingClientRect().top -
                            slot.getBoundingClientRect().top);
    price.style.marginTop = (offset > 0 && offset < 200) ? offset + 'px' : '';
  };

  // ----------------------------------------------------------- cart handoff

  SleeveDesigner.prototype.designNote = function () {
    var s = this.size(), f = this.finish(), st = this.state;
    var deg = Math.round(st.rot * 180 / Math.PI);
    var parts = [];

    // Shopify replaces the uploaded filename with a hash, so the customer's own
    // name for the file is recorded here. If a file is ever missing from an
    // order -- express checkout is the likely case -- this is what lets you ask
    // for the right thing by name instead of "please resend your artwork".
    if (st.file && st.file.name) parts.push(st.file.name);

    parts = parts.concat([
      st.sizeKey + ' ' + s.w + 'x' + s.h + 'mm',
      f.finish + (f.border ? '/' + f.border + '-border' : '/borderless'),
      'scale ' + st.scale.toFixed(2),
      'offset ' + st.tx.toFixed(1) + 'mm,' + st.ty.toFixed(1) + 'mm',
      'rotate ' + deg + 'deg'
    ]);
    if (this.lastCover) {
      parts.push('src ' + Math.round((MM_PER_INCH * PX_PER_MM) / this.lastCover) + 'ppi');
    }
    return parts.join(' | ');
  };

  SleeveDesigner.prototype.setFileOnInput = function (input, file) {
    if (!input) return;
    if (!file) { input.value = ''; return; }
    try {
      var dt = new DataTransfer();
      dt.items.add(file);
      input.files = dt.files;
    } catch (e) {
      console.warn('[sleeve-designer] could not attach file to form input', e);
    }
  };

  /**
   * Injects the line-item-property inputs into the product form.
   * Must be re-run after every variant change: product-info.js replaces the
   * innerHTML of the buy-buttons container, which destroys these inputs.
   */
  SleeveDesigner.prototype.ensureFormInputs = function () {
    var form = this.findForm();
    if (!form) return;
    this.form = form;

    var self = this;
    function ensure(key, type, name) {
      var found = form.querySelector('[data-sd-prop="' + key + '"]');
      if (!found) {
        found = document.createElement('input');
        found.type = type;
        found.name = name;
        found.setAttribute('data-sd-prop', key);
        found.className = 'sd-visually-hidden';
        if (type === 'file') found.setAttribute('tabindex', '-1');
        form.appendChild(found);
      }
      return found;
    }

    var art = ensure('art', 'file', 'properties[Artwork]');
    var note = ensure('note', 'hidden', 'properties[_design]');

    // Design Preview was removed from the order (2026-08-22): the designer
    // works from the original file plus the _design transforms, so the 3D
    // screenshot no longer needs to exist. Clean up any input left over from
    // a previous build of this script.
    var stale = form.querySelector('[data-sd-prop="shot"]');
    if (stale) stale.parentNode.removeChild(stale);

    this.setFileOnInput(art, this.state.file);
    note.value = this.state.file ? this.designNote() : '';

    if (!form.__sdGuard) {
      form.__sdGuard = true;
      form.addEventListener('submit', function (e) {
        if (!self.requireArtwork) return;

        if (!self.state.file) {
          e.preventDefault();
          e.stopImmediatePropagation();
          self.say('Please upload your design before adding to cart.', true);
          self.root.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }

      }, true);
    }
  };

  SleeveDesigner.prototype.updateGate = function () {
    if (!this.requireArtwork) return;
    var form = this.form || this.findForm();
    if (!form) return;
    var ready = !!this.state.file;
    // Includes the sticky button, which sits outside the form and is associated
    // via its form="" attribute. HTMLButtonElement.form resolves that for us.
    var all = document.querySelectorAll('[name="add"]');
    var btns = [];
    for (var n = 0; n < all.length; n++) {
      if (all[n].form === form) btns.push(all[n]);
    }
    for (var i = 0; i < btns.length; i++) {
      var b = btns[i];
      if (ready) {
        if (b.getAttribute('data-sd-blocked') === 'true') {
          b.removeAttribute('data-sd-blocked');
          b.removeAttribute('aria-disabled');
          b.classList.remove('sd-blocked');
        }
      } else {
        b.setAttribute('data-sd-blocked', 'true');
        b.setAttribute('aria-disabled', 'true');
        b.classList.add('sd-blocked');
      }
    }
    this.root.classList.toggle('sd--ready', ready);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);
})();
